import { z } from 'zod';
import { parseZonedDateTime } from '@internationalized/date';
import { differenceInMilliseconds } from 'date-fns';
import {
  AIRCRAFT_MAX_FLIGHT_TEXT,
  AIRCRAFT_REFUEL_WARNING_TEXT,
  MAX_BLOCKED_LEG_FLIGHT_HOURS,
  MAX_LEG_DRIVE_DURATION,
  MAX_LEG_FLIGHT_HOURS,
} from '@lp/features/RoutePlanner/config';
import {
  BaseAccommodationLegSchema,
  BaseGroundLegSchemaValues,
  BaseTmdxAviaLegSchema,
  CommonLegResourcesSchema,
  CommonRouteLegSchema,
  CommonRouteLegSchemaValues,
  LocationSchemaType,
} from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import { parseCurrencyStringUS } from '@lp/features/RoutePlanner/utils';
import { WARNING_MESSAGES_MAP, WarningTypeEnum } from '@lp/features/RoutePlanner/components/RouteLegForm/utils';
import { LegLocationTypeEnum, RouteLegTypeEnum, SegmentTypeEnum } from '@/api/apis/route';
import { CaseResourceType } from '@/api/apis/case';
import { addCustomZodErrors } from '@/utils/helpers';
import { GROUND_LEG_TYPES } from '@/utils/route';

const withLocationValidation = (
  type: 'departureLocation' | 'arrivalLocation',
  locationData: LocationSchemaType,
  legType: RouteLegTypeEnum,
  segmentType: SegmentTypeEnum,
  ctx: z.RefinementCtx,
) => {
  switch (locationData.locationType) {
    case LegLocationTypeEnum.hub:
      if (!locationData.id) {
        addCustomZodErrors({
          ctx,
          path: [`${type}.id`],
          message: type === 'arrivalLocation' ? 'Arrival hub is not assigned' : 'Departure hub is not assigned',
        });
      }
      break;
    case LegLocationTypeEnum.hospital:
    case LegLocationTypeEnum.txcenter:
    case LegLocationTypeEnum.homeResidence:
      if (!locationData.id) {
        addCustomZodErrors({
          ctx,
          path: [`${type}.id`],
          message: type === 'arrivalLocation' ? 'Select arrival location' : 'Select departure location',
        });
      }
      break;
    case LegLocationTypeEnum.airport:
      if (!locationData.id) {
        addCustomZodErrors({
          ctx,
          path: [`${type}.id`],
          message: type === 'arrivalLocation' ? 'Select arrival airport' : 'Select departure airport',
        });
      }

      const isCustomSegment = segmentType === SegmentTypeEnum.custom;
      const isCommercialFlight = legType === RouteLegTypeEnum.CommercialFlightAviaLeg;
      const isCustomGroundLeg = isCustomSegment && GROUND_LEG_TYPES.includes(legType);
      const isRequiredFbo = !isCommercialFlight && !isCustomGroundLeg;

      if (isRequiredFbo && !locationData.fboId) {
        addCustomZodErrors({
          ctx,
          path: [`${type}.fboId`],
          message: type === 'arrivalLocation' ? 'Select arrival FBO' : 'Select departure FBO',
        });
      }
      break;
    case LegLocationTypeEnum.custom:
      if (!locationData.hereMapsId) {
        addCustomZodErrors({
          ctx,
          path: [`${type}.hereMapsId`],
          message: type === 'arrivalLocation' ? 'Provide arrival address' : 'Provide departure address',
        });
      }
      break;
    default:
      break;
  }
};

export const withCommonValidation = (
  {
    departureDateTime,
    arrivalDateTime,
    departureLocation,
    arrivalLocation,
    legType,
    segmentType,
  }: z.infer<typeof CommonRouteLegSchema>,
  ctx: z.RefinementCtx,
) => {
  if (departureDateTime && arrivalDateTime) {
    const departureUtc = parseZonedDateTime(departureDateTime).toAbsoluteString();
    const arrivalUtc = parseZonedDateTime(arrivalDateTime).toAbsoluteString();
    const isArrivalTimeAfterDeparture = differenceInMilliseconds(arrivalUtc, departureUtc) >= 0;

    if (!isArrivalTimeAfterDeparture) {
      addCustomZodErrors({
        ctx,
        path: ['arrivalDateTimeValue'],
        message: `The arrival time must be on or after departure time`,
      });
    }
  }

  withLocationValidation('departureLocation', departureLocation, legType, segmentType, ctx);
  withLocationValidation('arrivalLocation', arrivalLocation, legType, segmentType, ctx);
};

export const withResourcesValidation = (
  { resources, hasSCS }: z.infer<typeof CommonLegResourcesSchema>,
  ctx: z.RefinementCtx,
) => {
  const hasResources = resources.some(({ resourceType }) =>
    [CaseResourceType.OCSConsole, CaseResourceType.ClinicalSpecialist, CaseResourceType.Surgeon].includes(resourceType),
  );

  if (!hasResources && !hasSCS) {
    addCustomZodErrors({
      ctx,
      path: ['resources'],
      message: 'At least one resource should be selected',
    });
  }
};

export const withTmdxAviaMaxFlightValidation = (
  { departureDateTime, arrivalDateTime, resources }: z.infer<typeof BaseTmdxAviaLegSchema>,
  ctx: z.RefinementCtx,
) => {
  const hasAircraft = resources.some(({ resourceType }) => resourceType === CaseResourceType.Aircraft);

  if (!hasAircraft) {
    addCustomZodErrors({
      ctx,
      path: ['selectedAircraftId'],
      message: 'Select aircraft',
    });
  }

  if (departureDateTime && arrivalDateTime) {
    const departureUtc = parseZonedDateTime(departureDateTime).toAbsoluteString();
    const arrivalUtc = parseZonedDateTime(arrivalDateTime).toAbsoluteString();
    const isCorrectArrivalTime = differenceInMilliseconds(arrivalUtc, departureUtc) <= MAX_BLOCKED_LEG_FLIGHT_HOURS;

    if (!isCorrectArrivalTime) {
      addCustomZodErrors({
        ctx,
        path: ['arrivalDateTimeValue'],
        message: AIRCRAFT_REFUEL_WARNING_TEXT,
      });
    }
  }
};

export const withAccommodationValidation = (
  { checkInAt, checkOutAt }: z.infer<typeof BaseAccommodationLegSchema>,
  ctx: z.RefinementCtx,
) => {
  if (checkInAt && checkOutAt) {
    const checkInUtc = parseZonedDateTime(checkInAt).toAbsoluteString();
    const checkOutUtc = parseZonedDateTime(checkOutAt).toAbsoluteString();
    const isCorrectCheckOutTime = differenceInMilliseconds(checkOutUtc, checkInUtc) >= 0;

    if (!isCorrectCheckOutTime) {
      addCustomZodErrors({
        ctx,
        path: ['checkOutAtValue'],
        message: `The check-out date must be on or after check-in date`,
      });
    }
  }
};

type CommonLegFields = Pick<
  CommonRouteLegSchemaValues,
  'legType' | 'minDate' | 'maxDate' | 'segmentType' | 'arrivalDateTime' | 'departureDateTime' | 'timeDurationInMs'
>;
type GroundLegFields = Pick<BaseGroundLegSchemaValues, 'isFeasibleForDrive'>;

export const validationLegWarning = ({
  legType,
  minDate,
  maxDate,
  segmentType,
  arrivalDateTime,
  departureDateTime,
  timeDurationInMs,
  isFeasibleForDrive,
}: CommonLegFields & GroundLegFields): string[] => {
  let warnings: string[] = [];

  if (departureDateTime && minDate) {
    const departureUtc = parseZonedDateTime(departureDateTime).toAbsoluteString();
    const minDateTz = parseZonedDateTime(minDate);
    const isCorrectDepartureTime = differenceInMilliseconds(departureUtc, minDateTz.toAbsoluteString()) >= 0;

    if (!isCorrectDepartureTime) {
      switch (segmentType) {
        case SegmentTypeEnum.hub:
          warnings = [...warnings, 'The departure time must be on or after TXC ETA Time + 1h'];
          break;
        case SegmentTypeEnum.txcenter:
          warnings = [...warnings, 'The departure time must be on or after OR time + operation time'];
          break;
        case SegmentTypeEnum.dhToHub:
          warnings = [...warnings, 'The departure time must be on or after OR time'];
          break;
        default:
          warnings = [...warnings, 'The departure time can not be in the past'];
          break;
      }
    }
  }

  if (arrivalDateTime && maxDate) {
    const arrivalUtc = parseZonedDateTime(arrivalDateTime).toAbsoluteString();
    const maxDateTz = parseZonedDateTime(maxDate);
    const isCorrectArrivalTime = differenceInMilliseconds(maxDateTz.toAbsoluteString(), arrivalUtc) >= 0;

    if (!isCorrectArrivalTime && segmentType === SegmentTypeEnum.hospital) {
      warnings = [...warnings, 'The arrival time must be 1h before OR time'];
    }
  }

  if (legType === RouteLegTypeEnum.TmdxJetAviaLeg && departureDateTime && arrivalDateTime) {
    const departureUtc = parseZonedDateTime(departureDateTime).toAbsoluteString();
    const arrivalUtc = parseZonedDateTime(arrivalDateTime).toAbsoluteString();
    const durationInMs = differenceInMilliseconds(arrivalUtc, departureUtc);

    if (durationInMs > MAX_LEG_FLIGHT_HOURS && durationInMs <= MAX_BLOCKED_LEG_FLIGHT_HOURS) {
      warnings = [...warnings, AIRCRAFT_MAX_FLIGHT_TEXT];
    }
  }

  if (
    [
      RouteLegTypeEnum.TmdxAffiliateGroundLeg,
      RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg,
      RouteLegTypeEnum.SelfServicedGroundLeg,
    ].includes(legType)
  ) {
    const isLongGroundLeg = timeDurationInMs > MAX_LEG_DRIVE_DURATION;

    if (isLongGroundLeg) {
      warnings = [...warnings, WARNING_MESSAGES_MAP[WarningTypeEnum.MaximumDriveTime]];
    }
  }

  const isGroundPartnerLeg = [
    RouteLegTypeEnum.TmdxAffiliateGroundLeg,
    RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg,
    RouteLegTypeEnum.TxCenterProviderGroundLeg,
    RouteLegTypeEnum.TmdxDriveTedsGroundLeg,
    RouteLegTypeEnum.SelfServicedGroundLeg,
  ].includes(legType);

  if (isGroundPartnerLeg && !isFeasibleForDrive) {
    warnings = [...warnings, WARNING_MESSAGES_MAP[WarningTypeEnum.GroundNotFeasible]];
  }

  return warnings;
};

export const refinePriceValidation = (value: string) => {
  if (!value) return true;

  const regex = /[0-9.]+/gm;

  return regex.exec('/[0-9.]+/g') && parseCurrencyStringUS(value) >= 1;
};

export const refinePriceRequiredValidation = (value: string) => {
  if (!value) return false;

  return refinePriceValidation;
};
